/**
 * Portfolio Preview Component
 *
 * Shows the artist's portfolio inside a recognisable device: an iPhone, an iPad or a MacBook.
 *
 * The site is rendered at the device's real viewport size and the whole device is scaled down to
 * fit the card. Narrowing the iframe instead (what this did before) is not a preview of anything:
 * "desktop" was whatever width the card happened to be, so the artist saw a layout no visitor
 * gets. Phone is the default because that is where most portfolio visits happen.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Laptop, Smartphone, Tablet, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

/** The editor's five colour roles, pushed into the page for a live, unsaved preview. */
export interface StylePreviewPalette {
  background: string
  text: string
  main: string
  second: string
  accent: string
}

interface PortfolioPreviewProps {
  url?: string
  className?: string
  /**
   * Restyle the framed page live, without saving (the profile app's StylePreviewBridge applies
   * it). `null` shows the page as served; `undefined` sends nothing at all.
   */
  stylePreview?: StylePreviewPalette | null
  /** Which device to open on (phone by default — where most portfolio visits happen). */
  defaultDevice?: 'mobile' | 'tablet' | 'desktop'
}

const PREVIEW_MESSAGE = 'arthaus:style-preview'
const READY_MESSAGE = 'arthaus:preview-ready'

function originOf(url: string): string | null {
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

type DeviceType = 'mobile' | 'tablet' | 'desktop'

interface DeviceSpec {
  label: string
  icon: React.ReactNode
  /** The viewport the site is rendered at, in CSS pixels. */
  width: number
  height: number
  /** Thickness of the black border around the screen. */
  bezel: number
  bodyRadius: number
  screenRadius: number
  /** Extra height below the screen (the MacBook's base). */
  baseHeight: number
  /**
   * Height of the phone's status bar. The site is rendered below it, as on a real phone; drawing
   * the camera pill over the page instead hid the first line of the artist's own header.
   */
  statusBar: number
}

const DEVICES: Record<DeviceType, DeviceSpec> = {
  mobile: {
    label: 'Phone',
    icon: <Smartphone className="h-4 w-4" />,
    width: 390,
    height: 844,
    bezel: 14,
    bodyRadius: 60,
    screenRadius: 46,
    baseHeight: 0,
    statusBar: 54,
  },
  tablet: {
    label: 'Tablet',
    icon: <Tablet className="h-4 w-4" />,
    width: 820,
    height: 1180,
    bezel: 26,
    bodyRadius: 44,
    screenRadius: 20,
    baseHeight: 0,
    statusBar: 0,
  },
  desktop: {
    label: 'Desktop',
    icon: <Laptop className="h-4 w-4" />,
    width: 1440,
    height: 900,
    bezel: 18,
    bodyRadius: 26,
    screenRadius: 8,
    baseHeight: 30,
    statusBar: 0,
  },
}

const DEVICE_ORDER: DeviceType[] = ['mobile', 'tablet', 'desktop']

/** Tallest the device may be drawn, so a phone does not push the rest of the page away. */
const MAX_STAGE_HEIGHT = 640
/** The MacBook's base is wider than its lid by this much on each side. */
const BASE_OVERHANG = 70

export function PortfolioPreview({ url, className, stylePreview, defaultDevice = 'mobile' }: PortfolioPreviewProps) {
  const [isLoading, setIsLoading] = useState(true)
  const frameRef = useRef<HTMLIFrameElement>(null)
  const pageOrigin = url ? originOf(url) : null

  // Push the palette on screen into the page: whenever it changes, and again when the page says it
  // is ready (it may have loaded after the last change was sent).
  const send = useCallback(() => {
    const win = frameRef.current?.contentWindow
    if (!win || !pageOrigin || stylePreview === undefined) return
    win.postMessage({ type: PREVIEW_MESSAGE, palette: stylePreview }, pageOrigin)
  }, [pageOrigin, stylePreview])

  useEffect(() => {
    send()
  }, [send])

  useEffect(() => {
    if (stylePreview === undefined || !pageOrigin) return
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== pageOrigin || event.source !== frameRef.current?.contentWindow) return
      if (event.data && event.data.type === READY_MESSAGE) send()
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [pageOrigin, send, stylePreview])
  const [device, setDevice] = useState<DeviceType>(defaultDevice)
  const stageRef = useRef<HTMLDivElement>(null)
  const [stageWidth, setStageWidth] = useState(0)

  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const measure = () => setStageWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [url])

  if (!url) {
    return (
      <div className={cn('flex items-center justify-center bg-muted/50 h-[500px]', className)}>
        <p className="text-muted-foreground">No preview available</p>
      </div>
    )
  }

  const spec = DEVICES[device]
  const overhang = spec.baseHeight ? BASE_OVERHANG : 0
  const lidWidth = spec.width + spec.bezel * 2
  const fullWidth = lidWidth + overhang * 2
  const fullHeight = spec.height + spec.bezel * 2 + spec.baseHeight
  // Until the stage has been measured, size by height alone; never scale a device up.
  const scale = Math.min(1, MAX_STAGE_HEIGHT / fullHeight, stageWidth ? stageWidth / fullWidth : 1)

  return (
    <div className={cn('relative', className)}>
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
        <div className="flex gap-1 rounded-lg border bg-background p-1" role="group" aria-label="Preview device">
          {DEVICE_ORDER.map((key) => (
            <Button
              key={key}
              variant={device === key ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 gap-1.5 px-2.5"
              aria-pressed={device === key}
              onClick={() => setDevice(key)}
            >
              {DEVICES[key].icon}
              <span className="text-xs">{DEVICES[key].label}</span>
            </Button>
          ))}
        </div>
        <Button asChild variant="ghost" size="sm">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4 mr-1" />
            Open
          </a>
        </Button>
      </div>

      <div className="bg-muted/30 px-4 py-6">
        <div ref={stageRef} className="flex justify-center">
          {/* Reserves the scaled footprint; the device itself is drawn at full size and scaled. */}
          <div style={{ width: fullWidth * scale, height: fullHeight * scale }}>
            <div
              data-testid="device-frame"
              data-device={device}
              className="relative"
              style={{
                width: fullWidth,
                height: fullHeight,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <div
                className="absolute top-0 bg-neutral-900 shadow-xl ring-1 ring-black/40"
                style={{
                  left: overhang,
                  width: lidWidth,
                  height: spec.height + spec.bezel * 2,
                  padding: spec.bezel,
                  borderRadius: spec.baseHeight
                    ? `${spec.bodyRadius}px ${spec.bodyRadius}px 0 0`
                    : spec.bodyRadius,
                }}
              >
                <div
                  className="relative h-full w-full overflow-hidden bg-black"
                  style={{ borderRadius: spec.screenRadius, paddingTop: spec.statusBar }}
                >
                  {/* iPhone: status bar with the camera pill, the time and the battery. */}
                  {spec.statusBar > 0 && (
                    <div
                      aria-hidden
                      className="absolute inset-x-0 top-0 flex items-center justify-between px-9 text-white"
                      style={{ height: spec.statusBar }}
                    >
                      <span className="text-[17px] font-semibold">9:41</span>
                      <span className="absolute left-1/2 top-[11px] h-8 w-28 -translate-x-1/2 rounded-full bg-neutral-800" />
                      <span className="h-3 w-7 rounded-[4px] border border-white/70 p-px">
                        <span className="block h-full w-4/5 rounded-[2px] bg-white" />
                      </span>
                    </div>
                  )}
                  {isLoading && <Skeleton className="absolute inset-0 h-full w-full rounded-none" />}
                  <iframe
                    ref={frameRef}
                    src={url}
                    className="block border-0 bg-background"
                    style={{ width: spec.width, height: spec.height - spec.statusBar }}
                    onLoad={() => {
                      setIsLoading(false)
                      send()
                    }}
                    title="Portfolio Preview"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                  />
                </div>

                {/* iPad and MacBook: the camera in the top border. */}
                {device !== 'mobile' && (
                  <div
                    aria-hidden
                    className="absolute left-1/2 -translate-x-1/2 rounded-full bg-neutral-700"
                    style={{ top: spec.bezel / 2 - 4, width: 8, height: 8 }}
                  />
                )}
              </div>

              {/* MacBook: the base the lid sits on. */}
              {spec.baseHeight > 0 && (
                <div
                  aria-hidden
                  className="absolute bottom-0 left-0 w-full bg-gradient-to-b from-neutral-200 to-neutral-400 shadow-lg"
                  style={{ height: spec.baseHeight, borderRadius: '4px 4px 22px 22px' }}
                >
                  <div
                    className="mx-auto bg-neutral-400/80"
                    style={{ width: 220, height: 12, borderRadius: '0 0 12px 12px' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
